import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Search,
  LayoutTemplate,
  MoreHorizontal,
  Pencil,
  Trash2,
  Copy,
  Eye,
  FolderOpen,
  FormInput,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  getGroupTemplate,
  listGroupTemplates,
  listAllGroupTemplatesWithFields,
  createGroupTemplate,
  updateGroupTemplate,
  deleteGroupTemplate,
  duplicateGroupTemplate,
  listFieldTemplates,
  createFieldTemplate,
  updateFieldTemplate,
  deleteFieldTemplate,
} from "@/lib/api/templates";
import { getErrorMessage } from "@/lib/api/client";
import { formatDate, slugify } from "@/lib/utils";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import type {
  FormFieldGroupTemplate,
  FormFieldGroupTemplateWithFields,
  FormFieldGroupTemplateCreate,
  FormFieldTemplate,
  FormFieldTemplateCreate,
  FieldType,
} from "@/types";
import { Switch } from "@/components/ui/switch";

const FIELD_TYPE_OPTIONS: { value: FieldType; label: string }[] = [
  { value: "text", label: "Texte" },
  { value: "number", label: "Nombre" },
  { value: "select", label: "Liste déroulante" },
  { value: "multiselect", label: "Sélection multiple" },
  { value: "checkbox", label: "Case à cocher" },
  { value: "date", label: "Date" },
  { value: "textarea", label: "Zone de texte" },
  { value: "radio", label: "Boutons radio" },
];

export const Route = createFileRoute('/_dashboard/admin/models')({
  component: AdminModelsPage,
})

function AdminModelsPage() {
  const [activeTab, setActiveTab] = useState<"groups" | "fields">("groups");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [editGroupDialogOpen, setEditGroupDialogOpen] = useState(false);
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [viewGroupDialogOpen, setViewGroupDialogOpen] = useState(false);
  
  const [createFieldDialogOpen, setCreateFieldDialogOpen] = useState(false);
  const [editFieldDialogOpen, setEditFieldDialogOpen] = useState(false);
  const [deleteFieldDialogOpen, setDeleteFieldDialogOpen] = useState(false);
  
  // New state for adding field to a group
  const [addFieldToGroupDialogOpen, setAddFieldToGroupDialogOpen] = useState(false);
  const [editFieldInGroupDialogOpen, setEditFieldInGroupDialogOpen] = useState(false);
  const [deleteFieldInGroupDialogOpen, setDeleteFieldInGroupDialogOpen] = useState(false);
  
  const [selectedGroup, setSelectedGroup] = useState<FormFieldGroupTemplateWithFields | null>(null);
  const [selectedField, setSelectedField] = useState<FormFieldTemplate | null>(null);
  
  const [groupFormData, setGroupFormData] = useState({
    name: "",
    slug: "",
    description: "",
  });
  
  const [fieldFormData, setFieldFormData] = useState<FormFieldTemplateCreate>({
    name: "",
    slug: "",
    label: "",
    description: "",
    field_type: "text" as FieldType,
    required: false,
    placeholder: "",
    tooltip: "",
  });

  const queryClient = useQueryClient();
  const pageSize = 10;

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ["group-templates", { page, page_size: pageSize, search }],
    queryFn: () => listGroupTemplates({ page, page_size: pageSize, search: search || undefined }),
    enabled: activeTab === "groups",
  });

  const { data: allGroupsWithFields } = useQuery({
    queryKey: ["group-templates-with-fields"],
    queryFn: listAllGroupTemplatesWithFields,
  });

  const { data: fieldsData, isLoading: fieldsLoading } = useQuery({
    queryKey: ["field-templates", { page, page_size: pageSize, search, standalone_only: true }],
    queryFn: () => listFieldTemplates({ page, page_size: pageSize, search: search || undefined, standalone_only: true }),
    enabled: activeTab === "fields",
  });

  const createGroupMutation = useMutation({
    mutationFn: (data: FormFieldGroupTemplateCreate) => createGroupTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-templates"] });
      queryClient.invalidateQueries({ queryKey: ["group-templates-with-fields"] });
      toast.success("Modèle de groupe créé");
      setCreateGroupDialogOpen(false);
      resetGroupForm();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<FormFieldGroupTemplate> }) =>
      updateGroupTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-templates"] });
      queryClient.invalidateQueries({ queryKey: ["group-templates-with-fields"] });
      toast.success("Modèle mis à jour");
      setEditGroupDialogOpen(false);
      resetGroupForm();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id: number) => deleteGroupTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-templates"] });
      queryClient.invalidateQueries({ queryKey: ["group-templates-with-fields"] });
      toast.success("Modèle supprimé");
      setDeleteGroupDialogOpen(false);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const duplicateGroupMutation = useMutation({
    mutationFn: (id: number) => duplicateGroupTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-templates"] });
      queryClient.invalidateQueries({ queryKey: ["group-templates-with-fields"] });
      toast.success("Modèle dupliqué");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Mutation for standalone fields
  const createFieldMutation = useMutation({
    mutationFn: (data: FormFieldTemplateCreate) => createFieldTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-templates"] });
      toast.success("Modèle de champ créé");
      setCreateFieldDialogOpen(false);
      resetFieldForm();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Mutation for adding field to a group
  const addFieldToGroupMutation = useMutation({
    mutationFn: (data: FormFieldTemplateCreate) => createFieldTemplate(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["group-templates"] });
      queryClient.invalidateQueries({ queryKey: ["group-templates-with-fields"] });
      queryClient.invalidateQueries({ queryKey: ["field-templates"] });
      toast.success("Champ ajouté au groupe");
      setAddFieldToGroupDialogOpen(false);
      resetFieldForm();
      // Refresh the selected group
      if (selectedGroup) {
        const updated = await getGroupTemplate(selectedGroup.id);
        setSelectedGroup(updated);
      }
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Mutation for editing field in a group
  const updateFieldInGroupMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<FormFieldTemplate> }) =>
      updateFieldTemplate(id, data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["group-templates"] });
      queryClient.invalidateQueries({ queryKey: ["group-templates-with-fields"] });
      queryClient.invalidateQueries({ queryKey: ["field-templates"] });
      toast.success("Champ mis à jour");
      setEditFieldInGroupDialogOpen(false);
      resetFieldForm();
      // Refresh the selected group
      if (selectedGroup) {
        const updated = await getGroupTemplate(selectedGroup.id);
        setSelectedGroup(updated);
      }
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Mutation for deleting field in a group
  const deleteFieldInGroupMutation = useMutation({
    mutationFn: (id: number) => deleteFieldTemplate(id),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["group-templates"] });
      queryClient.invalidateQueries({ queryKey: ["group-templates-with-fields"] });
      queryClient.invalidateQueries({ queryKey: ["field-templates"] });
      toast.success("Champ supprimé");
      setDeleteFieldInGroupDialogOpen(false);
      // Refresh the selected group
      if (selectedGroup) {
        const updated = await getGroupTemplate(selectedGroup.id);
        setSelectedGroup(updated);
      }
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateFieldMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<FormFieldTemplate> }) =>
      updateFieldTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-templates"] });
      toast.success("Modèle mis à jour");
      setEditFieldDialogOpen(false);
      resetFieldForm();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteFieldMutation = useMutation({
    mutationFn: (id: number) => deleteFieldTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-templates"] });
      toast.success("Modèle supprimé");
      setDeleteFieldDialogOpen(false);
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const resetGroupForm = () => {
    setGroupFormData({ name: "", slug: "", description: "" });
    setSelectedGroup(null);
  };

  const resetFieldForm = () => {
    setFieldFormData({
      name: "",
      slug: "",
      label: "",
      description: "",
      field_type: "text",
      required: false,
      placeholder: "",
      tooltip: "",
    });
    setSelectedField(null);
  };

  const handleGroupNameChange = (name: string) => {
    setGroupFormData({ ...groupFormData, name, slug: slugify(name) });
  };

  const handleFieldNameChange = (name: string) => {
    setFieldFormData({ ...fieldFormData, name, slug: slugify(name), label: name });
  };

  const handleEditGroup = (group: FormFieldGroupTemplateWithFields) => {
    setSelectedGroup(group);
    setGroupFormData({
      name: group.name,
      slug: group.slug,
      description: group.description || "",
    });
    setEditGroupDialogOpen(true);
  };

  const handleViewGroup = (group: FormFieldGroupTemplateWithFields) => {
    setSelectedGroup(group);
    setViewGroupDialogOpen(true);
  };

  const handleDeleteGroup = (group: FormFieldGroupTemplateWithFields) => {
    setSelectedGroup(group);
    setDeleteGroupDialogOpen(true);
  };

  const handleAddFieldToGroup = () => {
    resetFieldForm();
    setAddFieldToGroupDialogOpen(true);
  };

  const handleEditFieldInGroup = (field: FormFieldTemplate) => {
    setSelectedField(field);
    setFieldFormData({
      name: field.name,
      slug: field.slug,
      label: field.label,
      description: field.description || "",
      field_type: field.field_type,
      required: field.required,
      placeholder: field.placeholder || "",
      tooltip: field.tooltip || "",
      group_id: field.group_id,
    });
    setEditFieldInGroupDialogOpen(true);
  };

  const handleDeleteFieldInGroup = (field: FormFieldTemplate) => {
    setSelectedField(field);
    setDeleteFieldInGroupDialogOpen(true);
  };

  const handleEditField = (field: FormFieldTemplate) => {
    setSelectedField(field);
    setFieldFormData({
      name: field.name,
      slug: field.slug,
      label: field.label,
      description: field.description || "",
      field_type: field.field_type,
      required: field.required,
      placeholder: field.placeholder || "",
      tooltip: field.tooltip || "",
    });
    setEditFieldDialogOpen(true);
  };

  const handleDeleteField = (field: FormFieldTemplate) => {
    setSelectedField(field);
    setDeleteFieldDialogOpen(true);
  };

  const getFieldTypeLabel = (type: FieldType) => {
    return FIELD_TYPE_OPTIONS.find((opt) => opt.value === type)?.label || type;
  };

  const countGroupFields = (group: FormFieldGroupTemplateWithFields): number => {
    let count = group.fields.length;
    for (const child of group.children) {
      count += countGroupFields(child);
    }
    return count;
  };

  return (
    <AdminGuard>
      <div className="flex flex-col h-full">
        <Header title="Modèles de formulaires" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as "groups" | "fields"); setPage(1); }}>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <TabsList>
                <TabsTrigger value="groups" className="gap-2">
                  <FolderOpen className="h-4 w-4" />
                  Groupes de champs
                </TabsTrigger>
                <TabsTrigger value="fields" className="gap-2">
                  <FormInput className="h-4 w-4" />
                  Champs individuels
                </TabsTrigger>
              </TabsList>
              
              <div className="flex gap-2">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9"
                  />
                </div>
                {activeTab === "groups" ? (
                  <Button onClick={() => setCreateGroupDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Nouveau groupe
                  </Button>
                ) : (
                  <Button onClick={() => setCreateFieldDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Nouveau champ
                  </Button>
                )}
              </div>
            </div>

            <TabsContent value="groups" className="mt-6">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nom</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Champs</TableHead>
                        <TableHead>Mis à jour</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {groupsLoading ? (
                        [...Array(5)].map((_, i) => (
                          <TableRow key={i}>
                            <TableCell><Skeleton className="h-10 w-full" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                            <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                          </TableRow>
                        ))
                      ) : groupsData?.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="h-32 text-center">
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                              <LayoutTemplate className="h-10 w-10 opacity-50" />
                              <p>Aucun modèle de groupe</p>
                              <Button variant="outline" size="sm" onClick={() => setCreateGroupDialogOpen(true)}>
                                Créer un modèle
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        groupsData?.items.map((group, index) => {
                          const fullGroup = allGroupsWithFields?.find((g) => g.id === group.id);
                          const fieldCount = fullGroup ? countGroupFields(fullGroup) : 0;
                          
                          return (
                            <TableRow
                              key={group.id}
                              className="border-b transition-colors hover:bg-muted/50"
                              style={{
                                animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                              }}
                            >
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-lg bg-laposte-navy/10 flex items-center justify-center shrink-0">
                                    <FolderOpen className="h-5 w-5 text-laposte-navy" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-medium">{group.name}</p>
                                    {group.description && (
                                      <p className="text-sm text-muted-foreground line-clamp-1 max-w-xs">{group.description}</p>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <code className="px-2 py-1 bg-muted rounded text-xs">{group.slug}</code>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{fieldCount} champ{fieldCount !== 1 ? "s" : ""}</Badge>
                              </TableCell>
                              <TableCell className="text-muted-foreground">{formatDate(group.updated_at)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => fullGroup && handleViewGroup(fullGroup)} title="Gérer les champs">
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => fullGroup && handleEditGroup(fullGroup)}>
                                        <Pencil className="mr-2 h-4 w-4" />Modifier
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => duplicateGroupMutation.mutate(group.id)}>
                                        <Copy className="mr-2 h-4 w-4" />Dupliquer
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => fullGroup && handleDeleteGroup(fullGroup)} className="text-destructive focus:text-destructive">
                                        <Trash2 className="mr-2 h-4 w-4" />Supprimer
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              {groupsData && groupsData.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">{groupsData.total_items} modèle{groupsData.total_items > 1 ? "s" : ""}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Précédent</Button>
                    <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">{page} / {groupsData.total_pages}</div>
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(groupsData.total_pages, p + 1))} disabled={page === groupsData.total_pages}>Suivant</Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="fields" className="mt-6">
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nom</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Requis</TableHead>
                        <TableHead>Mis à jour</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fieldsLoading ? (
                        [...Array(5)].map((_, i) => (
                          <TableRow key={i}>
                            <TableCell><Skeleton className="h-10 w-full" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                            <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                          </TableRow>
                        ))
                      ) : fieldsData?.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="h-32 text-center">
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                              <FormInput className="h-10 w-10 opacity-50" />
                              <p>Aucun modèle de champ</p>
                              <Button variant="outline" size="sm" onClick={() => setCreateFieldDialogOpen(true)}>Créer un modèle</Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        fieldsData?.items.map((field, index) => (
                          <TableRow
                            key={field.id}
                            className="border-b transition-colors hover:bg-muted/50"
                            style={{
                              animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                            }}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-lg bg-info/10 flex items-center justify-center shrink-0">
                                  <FormInput className="h-5 w-5 text-info" />
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium">{field.label}</p>
                                  {field.description && <p className="text-sm text-muted-foreground line-clamp-1 max-w-xs">{field.description}</p>}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell><code className="px-2 py-1 bg-muted rounded text-xs">{field.slug}</code></TableCell>
                            <TableCell><Badge variant="outline">{getFieldTypeLabel(field.field_type)}</Badge></TableCell>
                            <TableCell>{field.required ? <Badge variant="default">Oui</Badge> : <span className="text-muted-foreground">Non</span>}</TableCell>
                            <TableCell className="text-muted-foreground">{formatDate(field.updated_at)}</TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleEditField(field)}><Pencil className="mr-2 h-4 w-4" />Modifier</DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleDeleteField(field)} className="text-destructive focus:text-destructive"><Trash2 className="mr-2 h-4 w-4" />Supprimer</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              {fieldsData && fieldsData.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">{fieldsData.total_items} modèle{fieldsData.total_items > 1 ? "s" : ""}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Précédent</Button>
                    <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">{page} / {fieldsData.total_pages}</div>
                    <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(fieldsData.total_pages, p + 1))} disabled={page === fieldsData.total_pages}>Suivant</Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Create Group Dialog */}
        <Dialog open={createGroupDialogOpen} onOpenChange={setCreateGroupDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau modèle de groupe</DialogTitle>
              <DialogDescription>Créez un modèle de groupe de champs réutilisable</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="group-name">Nom</Label>
                <Input id="group-name" value={groupFormData.name} onChange={(e) => handleGroupNameChange(e.target.value)} placeholder="Informations client" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group-slug">Slug</Label>
                <Input id="group-slug" value={groupFormData.slug} onChange={(e) => setGroupFormData({ ...groupFormData, slug: e.target.value })} placeholder="informations-client" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group-description">Description</Label>
                <Textarea id="group-description" value={groupFormData.description} onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })} placeholder="Description..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateGroupDialogOpen(false)}>Annuler</Button>
              <Button onClick={() => createGroupMutation.mutate(groupFormData)} disabled={createGroupMutation.isPending || !groupFormData.name || !groupFormData.slug}>
                {createGroupMutation.isPending ? "Création..." : "Créer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Group Dialog */}
        <Dialog open={editGroupDialogOpen} onOpenChange={setEditGroupDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier le modèle</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-group-name">Nom</Label>
                <Input id="edit-group-name" value={groupFormData.name} onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-group-description">Description</Label>
                <Textarea id="edit-group-description" value={groupFormData.description} onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditGroupDialogOpen(false)}>Annuler</Button>
              <Button onClick={() => selectedGroup && updateGroupMutation.mutate({ id: selectedGroup.id, data: { name: groupFormData.name, description: groupFormData.description } })} disabled={updateGroupMutation.isPending}>
                {updateGroupMutation.isPending ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View/Manage Group Dialog */}
        <Dialog open={viewGroupDialogOpen} onOpenChange={setViewGroupDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-laposte-navy" />
                  {selectedGroup?.name}
                </DialogTitle>
                <Button size="sm" onClick={handleAddFieldToGroup}>
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter un champ
                </Button>
              </div>
              <DialogDescription>{selectedGroup?.description || "Aucune description"}</DialogDescription>
            </DialogHeader>
            <ScrollArea className="flex-1 -mx-6 px-6">
              {selectedGroup && (
                <div className="space-y-4">
                  {selectedGroup.fields.length === 0 && selectedGroup.children.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
                      <FormInput className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p className="mb-3">Aucun champ dans ce modèle</p>
                      <Button variant="outline" size="sm" onClick={handleAddFieldToGroup}>
                        <Plus className="mr-2 h-4 w-4" />
                        Ajouter un champ
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Fields */}
                      {selectedGroup.fields.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-muted-foreground">Champs ({selectedGroup.fields.length})</h4>
                          <div className="space-y-2">
                            {selectedGroup.fields.map((field) => (
                              <div key={field.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card group">
                                <FormInput className="h-4 w-4 text-muted-foreground shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-sm">{field.label}</p>
                                  <p className="text-xs text-muted-foreground">{field.slug}</p>
                                </div>
                                <Badge variant="outline" className="text-xs shrink-0">{getFieldTypeLabel(field.field_type)}</Badge>
                                {field.required && <Badge variant="default" className="text-xs shrink-0">Requis</Badge>}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEditFieldInGroup(field)}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteFieldInGroup(field)}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Children groups */}
                      {selectedGroup.children.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="font-medium text-sm text-muted-foreground">Sous-groupes</h4>
                          <Accordion type="multiple" className="space-y-2">
                            {selectedGroup.children.map((child) => (
                              <AccordionItem key={child.id} value={String(child.id)} className="border rounded-lg">
                                <AccordionTrigger className="px-4 hover:no-underline">
                                  <div className="flex items-center gap-2">
                                    <FolderOpen className="h-4 w-4 text-laposte-navy" />
                                    <span>{child.name}</span>
                                    <Badge variant="secondary" className="ml-2">{child.fields.length} champ{child.fields.length !== 1 ? "s" : ""}</Badge>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className="px-4 pb-4">
                                  <div className="space-y-2">
                                    {child.fields.map((field) => (
                                      <div key={field.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                                        <FormInput className="h-4 w-4 text-muted-foreground" />
                                        <div className="flex-1 min-w-0"><p className="font-medium text-sm">{field.label}</p></div>
                                        <Badge variant="outline" className="text-xs">{getFieldTypeLabel(field.field_type)}</Badge>
                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </ScrollArea>
          </DialogContent>
        </Dialog>

        {/* Delete Group Dialog */}
        <Dialog open={deleteGroupDialogOpen} onOpenChange={setDeleteGroupDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-destructive" />Supprimer le modèle</DialogTitle>
              <DialogDescription>Êtes-vous sûr de vouloir supprimer &quot;{selectedGroup?.name}&quot; ? Cette action supprimera également tous les champs.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteGroupDialogOpen(false)}>Annuler</Button>
              <Button variant="destructive" onClick={() => selectedGroup && deleteGroupMutation.mutate(selectedGroup.id)} disabled={deleteGroupMutation.isPending}>
                {deleteGroupMutation.isPending ? "Suppression..." : "Supprimer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Field to Group Dialog */}
        <Dialog open={addFieldToGroupDialogOpen} onOpenChange={setAddFieldToGroupDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter un champ au groupe</DialogTitle>
              <DialogDescription>Ce champ sera ajouté au groupe &quot;{selectedGroup?.name}&quot;</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-field-name">Nom</Label>
                  <Input id="add-field-name" value={fieldFormData.name} onChange={(e) => handleFieldNameChange(e.target.value)} placeholder="Email client" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="add-field-slug">Slug</Label>
                  <Input id="add-field-slug" value={fieldFormData.slug} onChange={(e) => setFieldFormData({ ...fieldFormData, slug: e.target.value })} placeholder="email-client" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-field-label">Label</Label>
                <Input id="add-field-label" value={fieldFormData.label} onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })} placeholder="Adresse email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="add-field-type">Type</Label>
                  <Select value={fieldFormData.field_type} onValueChange={(v) => setFieldFormData({ ...fieldFormData, field_type: v as FieldType })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPE_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Requis</Label>
                  <div className="flex items-center h-10">
                    <Switch checked={fieldFormData.required} onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })} />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-field-placeholder">Placeholder</Label>
                <Input id="add-field-placeholder" value={fieldFormData.placeholder || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, placeholder: e.target.value })} placeholder="exemple@email.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-field-description">Description</Label>
                <Textarea id="add-field-description" value={fieldFormData.description || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, description: e.target.value })} placeholder="Description..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddFieldToGroupDialogOpen(false)}>Annuler</Button>
              <Button 
                onClick={() => selectedGroup && addFieldToGroupMutation.mutate({ ...fieldFormData, group_id: selectedGroup.id })} 
                disabled={addFieldToGroupMutation.isPending || !fieldFormData.name || !fieldFormData.slug || !fieldFormData.label}
              >
                {addFieldToGroupMutation.isPending ? "Ajout..." : "Ajouter"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Field in Group Dialog */}
        <Dialog open={editFieldInGroupDialogOpen} onOpenChange={setEditFieldInGroupDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier le champ</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-group-field-name">Nom</Label>
                  <Input id="edit-group-field-name" value={fieldFormData.name} onChange={(e) => setFieldFormData({ ...fieldFormData, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-group-field-label">Label</Label>
                  <Input id="edit-group-field-label" value={fieldFormData.label} onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-group-field-type">Type</Label>
                  <Select value={fieldFormData.field_type} onValueChange={(v) => setFieldFormData({ ...fieldFormData, field_type: v as FieldType })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPE_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Requis</Label>
                  <div className="flex items-center h-10">
                    <Switch checked={fieldFormData.required} onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })} />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-group-field-placeholder">Placeholder</Label>
                <Input id="edit-group-field-placeholder" value={fieldFormData.placeholder || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, placeholder: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-group-field-description">Description</Label>
                <Textarea id="edit-group-field-description" value={fieldFormData.description || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, description: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditFieldInGroupDialogOpen(false)}>Annuler</Button>
              <Button 
                onClick={() => selectedField && updateFieldInGroupMutation.mutate({ 
                  id: selectedField.id, 
                  data: { 
                    name: fieldFormData.name, 
                    label: fieldFormData.label, 
                    field_type: fieldFormData.field_type, 
                    required: fieldFormData.required, 
                    placeholder: fieldFormData.placeholder, 
                    description: fieldFormData.description 
                  } 
                })} 
                disabled={updateFieldInGroupMutation.isPending}
              >
                {updateFieldInGroupMutation.isPending ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Field in Group Dialog */}
        <Dialog open={deleteFieldInGroupDialogOpen} onOpenChange={setDeleteFieldInGroupDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-destructive" />Supprimer le champ</DialogTitle>
              <DialogDescription>Êtes-vous sûr de vouloir supprimer &quot;{selectedField?.label}&quot; du groupe ?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteFieldInGroupDialogOpen(false)}>Annuler</Button>
              <Button variant="destructive" onClick={() => selectedField && deleteFieldInGroupMutation.mutate(selectedField.id)} disabled={deleteFieldInGroupMutation.isPending}>
                {deleteFieldInGroupMutation.isPending ? "Suppression..." : "Supprimer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Standalone Field Dialog */}
        <Dialog open={createFieldDialogOpen} onOpenChange={setCreateFieldDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau modèle de champ</DialogTitle>
              <DialogDescription>Créez un modèle de champ individuel réutilisable</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="field-name">Nom</Label>
                  <Input id="field-name" value={fieldFormData.name} onChange={(e) => handleFieldNameChange(e.target.value)} placeholder="Email client" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="field-slug">Slug</Label>
                  <Input id="field-slug" value={fieldFormData.slug} onChange={(e) => setFieldFormData({ ...fieldFormData, slug: e.target.value })} placeholder="email-client" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-label">Label</Label>
                <Input id="field-label" value={fieldFormData.label} onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })} placeholder="Adresse email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="field-type">Type</Label>
                  <Select value={fieldFormData.field_type} onValueChange={(v) => setFieldFormData({ ...fieldFormData, field_type: v as FieldType })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPE_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Requis</Label>
                  <div className="flex items-center h-10">
                    <Switch checked={fieldFormData.required} onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })} />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-placeholder">Placeholder</Label>
                <Input id="field-placeholder" value={fieldFormData.placeholder || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, placeholder: e.target.value })} placeholder="exemple@email.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-description">Description</Label>
                <Textarea id="field-description" value={fieldFormData.description || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, description: e.target.value })} placeholder="Description..." rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateFieldDialogOpen(false)}>Annuler</Button>
              <Button onClick={() => createFieldMutation.mutate(fieldFormData)} disabled={createFieldMutation.isPending || !fieldFormData.name || !fieldFormData.slug || !fieldFormData.label}>
                {createFieldMutation.isPending ? "Création..." : "Créer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Standalone Field Dialog */}
        <Dialog open={editFieldDialogOpen} onOpenChange={setEditFieldDialogOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Modifier le modèle de champ</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-field-name">Nom</Label>
                  <Input id="edit-field-name" value={fieldFormData.name} onChange={(e) => setFieldFormData({ ...fieldFormData, name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-field-label">Label</Label>
                  <Input id="edit-field-label" value={fieldFormData.label} onChange={(e) => setFieldFormData({ ...fieldFormData, label: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-field-type">Type</Label>
                  <Select value={fieldFormData.field_type} onValueChange={(v) => setFieldFormData({ ...fieldFormData, field_type: v as FieldType })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FIELD_TYPE_OPTIONS.map((opt) => (<SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Requis</Label>
                  <div className="flex items-center h-10">
                    <Switch checked={fieldFormData.required} onCheckedChange={(checked) => setFieldFormData({ ...fieldFormData, required: checked })} />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-field-placeholder">Placeholder</Label>
                <Input id="edit-field-placeholder" value={fieldFormData.placeholder || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, placeholder: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-field-description">Description</Label>
                <Textarea id="edit-field-description" value={fieldFormData.description || ""} onChange={(e) => setFieldFormData({ ...fieldFormData, description: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditFieldDialogOpen(false)}>Annuler</Button>
              <Button onClick={() => selectedField && updateFieldMutation.mutate({ id: selectedField.id, data: { name: fieldFormData.name, label: fieldFormData.label, field_type: fieldFormData.field_type, required: fieldFormData.required, placeholder: fieldFormData.placeholder, description: fieldFormData.description } })} disabled={updateFieldMutation.isPending}>
                {updateFieldMutation.isPending ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Standalone Field Dialog */}
        <Dialog open={deleteFieldDialogOpen} onOpenChange={setDeleteFieldDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-destructive" />Supprimer le modèle</DialogTitle>
              <DialogDescription>Êtes-vous sûr de vouloir supprimer &quot;{selectedField?.label}&quot; ?</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteFieldDialogOpen(false)}>Annuler</Button>
              <Button variant="destructive" onClick={() => selectedField && deleteFieldMutation.mutate(selectedField.id)} disabled={deleteFieldMutation.isPending}>
                {deleteFieldMutation.isPending ? "Suppression..." : "Supprimer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminGuard>
  );
}
